import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { usePublicSettings } from 'Domains/settings/usePublicSettings.ts';
import { FADE_IN_UP } from 'Helpers/animations.ts';
import homegrownFullLogoSRC from 'Assets/homegrown-logo-full.png';
import maintenanceIconSRC from 'Assets/svg/maintenance.svg';

import { SUPPORT_EMAIL, clientAppRoot } from '../constants.ts';

type Variant = 'maintenance' | 'investor_registration';

const VARIANT_CONTENT: Record<Variant, { tag: string; title: string; description: ReactNode }> = {
    maintenance: {
        tag: 'Maintenance',
        title: "We'll Be Back Shortly",
        description: (
            <>
                The Homegrown platform is temporarily offline for maintenance. We're working to get
                everything back up as quickly as possible.
                <br />
                <br />
                Please check again in a little while, thank you!
            </>
        ),
    },
    investor_registration: {
        tag: 'Registration Paused',
        title: 'Investor Registration Is Currently Closed',
        description: (
            <>
                We are not accepting new investor registrations at this time.
                <br />
                <br />
                Stay tuned for updates — we look forward to welcoming new investors soon!
            </>
        ),
    },
};

type MaintenancePageProps = {
    variant?: Variant;
};

export function MaintenancePage({ variant = 'maintenance' }: MaintenancePageProps) {
    const { maintenance, investorRegistration, loading } = usePublicSettings();

    if (loading) {
        return null;
    }

    const isActive = variant === 'maintenance' ? maintenance : !investorRegistration;

    if (!isActive) {
        return <Navigate to={clientAppRoot} replace />;
    }

    const { tag, title, description } = VARIANT_CONTENT[variant];

    return (
        <div className="relative min-h-screen px-4 flex items-center justify-center overflow-hidden bg-background font-Roboto">
            <img
                src={homegrownFullLogoSRC}
                alt="Homegrown"
                className={`absolute top-6 left-6 h-10 ${FADE_IN_UP} [animation-delay:100ms]`}
            />

            <div className="w-full max-w-[550px] flex flex-col items-center gap-[30px] min-[600px]:gap-[43px]">
                <img
                    src={maintenanceIconSRC}
                    alt=""
                    className={`size-20 min-[600px]:size-[107px] ${FADE_IN_UP} [animation-delay:200ms]`}
                />

                <div className="w-full flex flex-col gap-4 text-center">
                    <p
                        className={`text-[20px] uppercase font-Manrope font-semibold tracking-[0.15em] text-warning ${FADE_IN_UP} [animation-delay:350ms]`}
                    >
                        {tag}
                    </p>

                    <p
                        className={`text-[26px] min-[600px]:text-[32px] font-Telegraf font-normal tracking-[0.00938em] text-foreground ${FADE_IN_UP} [animation-delay:500ms]`}
                    >
                        {title}
                    </p>

                    <p
                        className={`text-[18px] font-normal tracking-[0.00938em] leading-[1.4] text-foreground ${FADE_IN_UP} [animation-delay:650ms]`}
                    >
                        {description}
                    </p>
                </div>
            </div>

            <p
                className={`absolute bottom-6 text-center text-[16px] font-normal tracking-[0.00938em] text-muted-foreground ${FADE_IN_UP} [animation-delay:850ms]`}
            >
                You can reach out to us at
                <br />
                <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="underline text-info hover:text-info/80 transition-colors duration-200"
                >
                    {SUPPORT_EMAIL}
                </a>
            </p>
        </div>
    );
}
